import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiHome, FiMapPin } from 'react-icons/fi';
import { BsCheckCircle, BsXCircle, BsClock, BsCurrencyDollar, BsPerson, BsTrash, BsArchive, BsGeoAlt, BsFlag, BsEye } from 'react-icons/bs';
import { useCurrency } from '../../hooks/useCurrency';
import { useCountdown } from '../../hooks/useCountdown';
import { DispatchTask } from '../../services/dispatchService';

interface TaskCardProps {
    task: DispatchTask;
    variant: 'match' | 'posted';
    onAccept?: (taskId: string) => void;
    onApply?: (taskId: string) => void;
    onDecline?: (taskId: string) => void;
    onDelete?: (taskId: string) => void;
    onStatusChange?: (taskId: string, status: string) => void;
    onReport?: (taskId: string) => void;
    onClick?: (task: DispatchTask) => void;
    onEdit?: (task: DispatchTask) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, variant, onAccept, onApply, onDecline, onDelete, onStatusChange, onReport, onClick, onEdit }) => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    const timeLeft = useCountdown(task.expiresAt);

    const handleCardClick = (e: React.MouseEvent) => {
        // Prevent click if clicking a button
        if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('a')) {
            return;
        }
        onClick?.(task);
    };
    const getStatusBadge = () => {
        const statusConfig: Record<string, { label: string; color: string }> = {
            pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50' },
            dispatched: { label: 'Dispatched', color: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
            assigned: { label: 'Position Filled', color: 'bg-green-500/20 text-green-300 border-green-500/50' },
            declined: { label: 'Declined', color: 'bg-red-500/20 text-red-300 border-red-500/50' },
            expired: { label: 'Expired', color: 'bg-gray-500/20 text-gray-300 border-gray-500/50' }
        };

        const config = statusConfig[task.status] || statusConfig.pending;

        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const formatBudget = () => {
        if (!task.budgetRange) return null;
        const { min, max } = task.budgetRange;
        
        if (min && max) return `${formatPrice(min)} - ${formatPrice(max)}`;
        if (min) return `${t('dispatch.budget_from')} ${formatPrice(min)}`;
        if (max) return `${t('dispatch.budget_up_to')} ${formatPrice(max)}`;
        return null;
    };

    const getCategoryLabel = () => {
        const labels: Record<string, string> = {
            professional: 'Professional Gig',
            'semi-skilled': 'Skilled Labor',
            informal: 'Community Gig',
            emergency: 'Instant Task'
        };
        return (labels[task.category || 'professional'] || 'Professional').toUpperCase();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={handleCardClick}
            className={`glass-effect rounded-fluid-xl p-4 sm:p-6 hover:shadow-[0_0_30px_rgba(212,175,55,0.2)] transition-all duration-300 ${onClick ? 'cursor-pointer hover:bg-white/5' : ''}`}
        >
            {/* Header */}
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-start sm:justify-between mb-2">
                <div className="flex-1">
                    {/* Urgency Badge */}
                    {task.urgency === 'immediate' && (
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-red-500/20 text-red-400 border border-red-500/30 mb-2">
                            <FiAlertCircle className="w-3.5 h-3.5" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                                {t('dispatch.urgent')}
                            </span>
                        </div>
                    )}
                    
                    {/* Expiration Countdown */}
                    {task.expiresAt && (
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20 mb-2 ml-2">
                            <BsClock className="w-3 h-3" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                                {t('dispatch.expires_in')}: {timeLeft}
                            </span>
                        </div>
                    )}

                    {/* Active Viewers Social Proof */}
                    {task.activeViewers && task.activeViewers.length > 0 && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 text-[10px] border border-blue-500/20 mb-2 ml-2">
                            <BsEye className="w-3 h-3" />
                            <span>{task.activeViewers.length} {t('dispatch.viewing_now')}</span>
                        </div>
                    )}
                    {/* Category Badge */}
                    <div className="flex gap-2 mb-1">
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${task.category === 'professional' ? 'text-blue-300' :
                            task.category === 'informal' ? 'text-green-300' :
                                task.category === 'emergency' ? 'text-red-300' : 'text-purple-300'
                            }`}>
                            {getCategoryLabel()}
                        </span>
                        <span className="text-white/20 text-[10px]">•</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/60">
                            {task.mode?.toUpperCase() || 'REMOTE'}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <h3 className="text-lg sm:text-xl font-serif font-bold text-premium-gold leading-tight">
                            {task.title}
                        </h3>
                        
                        {/* Match Score */}
                        {variant === 'match' && task.matchScores && (
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/30 text-emerald-400" title="AI Match Score">
                                <span className="text-[10px] font-bold">
                                    {(task.matchScores[0]?.score * 100).toFixed(0)}% Match
                                </span>
                            </div>
                        )}
                        {/* Verified Employer Badge for On-Site Informal/Emergency Work */}
                        {['informal', 'emergency'].includes(task.category || '') &&
                            task.mode === 'on-site' &&
                            task.postedBy?.isEmployerVerified && (
                                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 border border-green-500/40 text-green-400">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-[9px] font-bold uppercase tracking-wider">Verified</span>
                                </div>
                            )}
                    </div>
                </div>
                <div className="sm:ml-4 flex-shrink-0 mt-2 sm:mt-0">
                    {getStatusBadge()}
                </div>
            </div>

            {/* Description */}
            <p className="text-white/80 mb-4 line-clamp-2 text-sm">
                {task.description}
            </p>

            {/* Skills & Tags */}
            <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                    {task.requiredSkills.slice(0, 3).map((skill) => (
                        <span
                            key={skill._id}
                            className="px-2 py-0.5 bg-premium-gold/10 text-premium-gold rounded text-[10px] font-medium border border-premium-gold/20"
                        >
                            {skill.name}
                        </span>
                    ))}
                    {task.requiredSkills.length > 3 && (
                        <span className="px-2 py-0.5 text-white/40 text-[10px] font-medium">
                            +{task.requiredSkills.length - 3} more
                        </span>
                    )}
                </div>
            </div>

            {/* Additional Requirements Tags */}
            {(task.toolsRequired || (task.physicalEffort && task.physicalEffort !== 'none') || (task.languageRequirements && task.languageRequirements.length > 0)) && (
                <div className="flex flex-wrap gap-2 mb-4">
                    {task.toolsRequired && (
                        <span className="px-2 py-0.5 bg-white/5 text-white/60 text-[10px] rounded border border-white/10 flex items-center gap-1" title="Tools Required">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500/50"></span>
                            <span>Tools Needed</span>
                        </span>
                    )}
                    {task.physicalEffort && task.physicalEffort !== 'none' && (
                        <span className="px-2 py-0.5 bg-white/5 text-white/60 text-[10px] rounded border border-white/10 capitalize">
                            Effort: {task.physicalEffort}
                        </span>
                    )}
                    {task.languageRequirements && task.languageRequirements.length > 0 && (
                        <span className="px-2 py-0.5 bg-white/5 text-white/60 text-[10px] rounded border border-white/10 truncate max-w-[150px]" title={task.languageRequirements.join(', ')}>
                            🗣 {task.languageRequirements[0]} {task.languageRequirements.length > 1 && `+${task.languageRequirements.length - 1}`}
                        </span>
                    )}
                </div>
            )}

            {/* Category-Specific Meta Info */}
            {['professional', 'semi-skilled'].includes(task.category || 'professional') ? (
                // PROFESSIONAL TASKS: Show Tech Stack & Timeline
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-4 pt-4 border-t border-white/5">
                    {/* Tech Stack / Skills */}
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-blue-300">
                            <BsPerson />
                        </div>
                        <div>
                            <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Stack</p>
                            <p className="text-xs text-white font-medium">
                                {task.requiredSkills.slice(0, 2).map(s => s.name).join(', ') || 'General'}
                            </p>
                        </div>
                    </div>

                    {/* Budget */}
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-premium-gold">
                            <BsCurrencyDollar />
                        </div>
                        <div>
                            <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Budget</p>
                            <p className="text-xs text-white font-medium">
                                {formatBudget() || 'Negotiable'}
                            </p>
                        </div>
                    </div>

                    {/* Location / Mode */}
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center overflow-hidden relative">
                            {task.mode === 'remote' ? (
                                <FiHome className="w-5 h-5 text-blue-300" />
                            ) : task.mode === 'on-site' ? (
                                <FiMapPin className="w-5 h-5 text-blue-300" />
                            ) : (
                                <BsGeoAlt className="text-blue-300" />
                            )}
                        </div>
                        <div>
                            <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Location</p>
                            <p className="text-xs text-white font-medium">
                                {task.location?.city ? `${task.location.city}, ${task.location.country}` : (task.mode === 'remote' ? 'Remote' : 'On-Site')}
                            </p>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-purple-300">
                            <BsClock />
                        </div>
                        <div>
                            <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Timeline</p>
                            <p className="text-xs text-white font-medium">
                                {task.timeline || 'Flexible'}
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                // INFORMAL/EMERGENCY TASKS: Show Daily Rate & Distance
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-4 pt-4 border-t border-white/5">
                    {/* Daily/Hourly Rate (Prominent) */}
                    <div className="flex items-center gap-2 col-span-2 bg-premium-gold/5 p-3 rounded-lg border border-premium-gold/20">
                        <div className="w-10 h-10 rounded bg-premium-gold/20 flex items-center justify-center text-premium-gold">
                            <BsCurrencyDollar className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-xs text-premium-gold/80 uppercase font-bold tracking-wider">{task.payStructure === 'hourly' ? 'Hourly Rate' : 'Daily Rate'}</p>
                            <p className="text-lg text-premium-gold font-bold">
                                {formatBudget() || 'Pay on Completion'}
                            </p>
                        </div>
                    </div>

                    {/* Distance (if location available) */}
                    {task.location?.coordinates && (
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-green-300">
                                <BsGeoAlt />
                            </div>
                            <div>
                                <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Distance</p>
                                <p className="text-xs text-white font-medium">
                                    {task.location.city || 'Nearby'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Duration / Timeline */}
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-purple-300">
                            <BsClock />
                        </div>
                        <div>
                            <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Duration</p>
                            <p className="text-xs text-white font-medium">
                                {task.timeline || task.type || 'One-Day'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Posted By (for matches) */}
            {/* Posted By (for matches/open tasks) */}
            {variant === 'match' && task.postedBy && (
                <div className="mb-4 pb-4 border-b border-premium-gold/20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-premium-gold/20 flex items-center justify-center text-premium-gold border border-premium-gold/30">
                            <span className="font-bold text-xs">
                                {task.postedBy.firstName?.[0]}{task.postedBy.lastName?.[0]}
                            </span>
                        </div>
                        <div>
                            <p className="text-white/60 text-[10px] uppercase tracking-tighter">Posted by</p>
                            <p className="text-white font-medium text-sm">
                                {task.postedBy.firstName} {task.postedBy.lastName}
                            </p>
                        </div>
                    </div>
                    {onReport && task.status === 'dispatched' && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onReport(task._id); }}
                            className="text-white/30 hover:text-red-400 transition-colors p-2 rounded-lg hover:bg-red-500/10"
                            title="Report Task"
                        >
                            <BsFlag className="w-4 h-4" />
                        </button>
                    )}
                </div>
            )}

            {/* Actions */}
            {/* Actions for Candidates (Apply/Decline) */}
            {variant === 'match' && task.status === 'dispatched' && (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-premium-gold/20">
                    {onDecline && (
                        <button
                            onClick={() => onDecline(task._id)}
                            className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                        >
                            <BsXCircle className="w-5 h-5" />
                            Decline
                        </button>
                    )}
                    {onAccept && (
                        <button
                            onClick={() => onAccept(task._id)}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold rounded-lg hover:shadow-[0_0_20px_rgba(72,187,120,0.5)] transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                        >
                            <BsCheckCircle className="w-5 h-5" />
                            Accept Match
                        </button>
                    )}
                    {onApply && (
                        <button
                            onClick={() => onApply(task._id)}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                        >
                            <BsCheckCircle className="w-5 h-5" />
                            Apply Now
                        </button>
                    )}
                </div>
            )}

            {/* Actions for Task Owners (Manage) */}
            {variant === 'posted' && task.status === 'dispatched' && (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-premium-gold/20">
                    {onEdit && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(task); }}
                            className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                        </button>
                    )}
                    {onStatusChange && (
                        <button
                            onClick={() => onStatusChange(task._id, 'expired')} // Expired = Taken/Closed
                            className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                            title="Mark as Taken/Closed"
                        >
                            <BsArchive className="w-4 h-4" />
                            Mark Taken
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={() => onDelete(task._id)}
                            className="flex-1 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                        >
                            <BsTrash className="w-4 h-4" />
                            Delete
                        </button>
                    )}
                </div>
            )}

            {/* Assigned Info */}
            {variant === 'posted' && task.status === 'assigned' && task.assignedTo && (
                <div className="pt-4 border-t border-premium-gold/20">
                    <p className="text-white/70 text-sm">
                        Assigned to <span className="text-premium-gold font-medium">
                            {task.assignedTo.firstName} {task.assignedTo.lastName}
                        </span>
                    </p>
                    {task.projectId && (
                        <a
                            href={`/#/projects/${task.projectId}`}
                            className="mt-2 inline-block text-premium-gold text-xs sm:text-sm hover:underline"
                        >
                            → View Project Workspace
                        </a>
                    )}
                </div>
            )}
        </motion.div>
    );
};

export default TaskCard;
